import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Film, Play } from 'lucide-react';
import { loadGalleryImages } from '../../utils/s3Utils';
import { fetchGalleryFolders, fetchFolderContents } from '../../services/galleryService';
import ImagePortal from './ImagePortal';
import VideoPortal from './VideoPortal';
import SlideshowPortal from './SlideshowPortal';
import './GalleryView.css';

const GalleryView = ({ onBack }) => {
    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [folderImages, setFolderImages] = useState({});
    const [expandedFolderImages, setExpandedFolderImages] = useState([]);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSlideshowActive, setIsSlideshowActive] = useState(false);
    const [slideshowMedia, setSlideshowMedia] = useState([]);
    const [isSlideshowLoading, setIsSlideshowLoading] = useState(false);

    const MediaPreview = ({ item, className }) => {
        if (!item) return null;

        const isVideo = item.type === 'video';
        return (
            <div className={`media-preview-container ${className}`}>
                <img
                    src={item.thumbnailPath}
                    alt={isVideo ? 'Video thumbnail' : 'Image preview'}
                    className={className}
                />
                {isVideo && (
                    <div className="video-indicator">
                        <Film size={16} />
                    </div>
                )}
            </div>
        );
    };

    const getSlideshowMedia = async () => {
        if (selectedFolder) {
            // If in a folder, show all media from that folder
            return expandedFolderImages;
        } else {
            // If on main page, collect all preview images from all folders
            const allMedia = [];
            for (const folder of folders) {
                const contents = await fetchFolderContents(folder.path);
                allMedia.push(...contents);
            }
            return allMedia;
        }
    };

    const handleSlideshowStart = async () => {
        if (selectedFolder) {
            // If in a folder, use existing expanded folder images
            setSlideshowMedia(expandedFolderImages);
            setIsSlideshowActive(true);
        } else {
            // If on main page, fetch all media from all folders
            setIsSlideshowLoading(true);
            try {
                const allMedia = [];
                for (const folder of folders) {
                    const contents = await fetchFolderContents(folder.path);
                    allMedia.push(...contents);
                }
                setSlideshowMedia(allMedia);
                setIsSlideshowActive(true);
            } catch (error) {
                console.error('Error loading slideshow media:', error);
                setError('Failed to load slideshow media.');
            } finally {
                setIsSlideshowLoading(false);
            }
        }
    };

    useEffect(() => {
        const loadGalleryFolders = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const response = await fetchGalleryFolders();
                console.log('Folders response:', response); // Debug log

                const sortedFolders = response.folders
                    .filter(folder => folder.name && folder.name.length > 0)
                    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

                setFolders(sortedFolders);

                // Load preview images for each folder
                const imagesByFolder = {};
                for (const folder of sortedFolders) {
                    try {
                        const previewImages = await loadGalleryImages({
                            folderName: folder.path,
                            limit: 4,
                            useThumbnails: true
                        });

                        if (previewImages.length > 0) {
                            imagesByFolder[folder.path] = previewImages;
                        }
                    } catch (error) {
                        console.error(`Error loading preview images for folder: ${folder.path}`, error);
                    }
                }

                setFolderImages(imagesByFolder);
            } catch (error) {
                console.error('Error loading gallery:', error);
                setError('Failed to load gallery. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        loadGalleryFolders();
    }, []);

    useEffect(() => {
        const loadFolderContents = async () => {
            if (!selectedFolder) return;

            setIsLoading(true);
            try {
                const contents = await fetchFolderContents(selectedFolder.path);
                setExpandedFolderImages(contents);
            } catch (error) {
                console.error('Error loading folder images:', error);
                setError('Failed to load folder images.');
            } finally {
                setIsLoading(false);
            }
        };

        loadFolderContents();
    }, [selectedFolder]);

    const handleMediaClick = (item, index) => {
        setSelectedMedia(item);
        setSelectedMediaIndex(index);
    };

    const handlePreviousMedia = () => {
        const newIndex = selectedMediaIndex === 0 ? expandedFolderImages.length - 1 : selectedMediaIndex - 1;
        const newMedia = expandedFolderImages[newIndex];
        setSelectedMediaIndex(newIndex);
        setSelectedMedia(newMedia);
    };

    const handleNextMedia = () => {
        const newIndex = selectedMediaIndex === expandedFolderImages.length - 1 ? 0 : selectedMediaIndex + 1;
        const newMedia = expandedFolderImages[newIndex];
        setSelectedMediaIndex(newIndex);
        setSelectedMedia(newMedia);
    };

    const handleBulkDownload = async () => {
        try {
            const files = expandedFolderImages.map(item => item.originalPath);

            for (const fileUrl of files) {
                try {
                    const response = await fetch(fileUrl);
                    const blob = await response.blob();
                    const filename = fileUrl.split('/').pop();

                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                } catch (error) {
                    console.error(`Failed to download file: ${fileUrl}`, error);
                }
            }
        } catch (error) {
            console.error('Error during bulk download:', error);
            setError('Failed to download files. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <div className="gallery-view">
                <div className="loading-container">
                    Loading...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="gallery-view">
                <div className="error-container">
                    <div className="error-message">{error}</div>
                    <button onClick={onBack} className="portal-button">
                        <ArrowLeft size={24} />
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="gallery-view">
            <div className={`gallery-wrapper ${selectedFolder ? 'folder-view' : ''}`}>
                <div className={`gallery-header ${selectedFolder ? 'folder-view' : ''}`}>
                    <div className="header-left">
                        <button
                            onClick={selectedFolder ? () => setSelectedFolder(null) : onBack}
                            className="portal-button"
                        >
                            <ArrowLeft size={24}/>
                        </button>
                        <div className={`title-container ${selectedFolder ? 'folder-title-container' : ''}`}>
                            <h1 className={`gallery-title ${selectedFolder ? 'folder-gallery-title' : ''}`}>
                                {selectedFolder ? selectedFolder.name : 'Photo Gallery'}
                            </h1>
                            {selectedFolder && (
                                <div className="folder-metadata">
                                    {selectedFolder.uploadedBy && (
                                        <div className="metadata-item">
                                            <span className="metadata-label">Uploaded By:</span>
                                            <span className="metadata-value">{selectedFolder.uploadedBy}</span>
                                        </div>
                                    )}
                                    {selectedFolder.description && (
                                        <div className="metadata-item">
                                            <span className="metadata-label">Description:</span>
                                            <span className="metadata-value">{selectedFolder.description}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="header-right">
                        {/* Show play button if we have any media to display */}
                        {(selectedFolder ? expandedFolderImages.length > 0 : Object.keys(folderImages).length > 0) && (
                            <button
                                onClick={handleSlideshowStart}
                                className="portal-button"
                                title="Start slideshow"
                            >
                                <Play size={24}/>
                            </button>
                        )}
                        {/* Only show download button in folder view */}
                        {selectedFolder && (
                            <button
                                onClick={handleBulkDownload}
                                className="portal-button"
                                title="Download all files"
                            >
                                <Download size={24}/>
                            </button>
                        )}
                    </div>
                </div>

                <div className="gallery-content">
                    {!selectedFolder ? (
                        <div className="folders-grid">
                            {folders.map((folder, index) => (
                                <div
                                    key={index}
                                    className="folder-card group"
                                    onClick={() => setSelectedFolder(folder)}
                                >
                                    <div className="folder-header">
                                        <h2 className="folder-title">{folder.name}</h2>
                                        <div className="folder-uploader">Uploaded by {folder.uploadedBy}</div>
                                    </div>
                                    <div className="folder-preview">
                                        {(folderImages[folder.path] || []).slice(0, 2).map((item, imgIndex) => (
                                            <div
                                                key={imgIndex}
                                                className="preview-image-container"
                                            >
                                                <MediaPreview
                                                    item={item}
                                                    className="preview-image"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="folder-overlay group-hover:opacity-100"/>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="images-grid">
                            {expandedFolderImages.map((item, index) => (
                                <div
                                    key={index}
                                    className="image-card group"
                                    onClick={() => handleMediaClick(item, index)}
                                >
                                    <MediaPreview
                                        item={item}
                                        className="gallery-image"
                                    />
                                    <div className="image-overlay group-hover:opacity-100"/>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {selectedMedia && (
                    selectedMedia.type === 'video' ? (
                        <VideoPortal
                            video={selectedMedia.originalPath}
                            onClose={() => {
                                setSelectedMedia(null);
                                setSelectedMediaIndex(null);
                            }}
                            onPrevious={handlePreviousMedia}
                            onNext={handleNextMedia}
                            totalItems={expandedFolderImages.length}
                            currentIndex={selectedMediaIndex}
                        />
                    ) : (
                        <ImagePortal
                            image={selectedMedia.originalPath}
                            onClose={() => {
                                setSelectedMedia(null);
                                setSelectedMediaIndex(null);
                            }}
                            onPrevious={handlePreviousMedia}
                            onNext={handleNextMedia}
                            totalImages={expandedFolderImages.length}
                            currentIndex={selectedMediaIndex}
                        />
                    )
                )}

                {isSlideshowActive && (
                    <SlideshowPortal
                        media={slideshowMedia}
                        onClose={() => setIsSlideshowActive(false)}
                        startIndex={selectedMediaIndex || 0}
                    />
                )}
            </div>
        </div>
    );
};

export default GalleryView;